import { Fragment } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, MenuIcon } from "@heroicons/react/outline";
import { ReactComponent as InstaCureLogo } from "Assets/InstacureLightLogo.svg";
import { useGlobalContext } from "Context/Global";
import GlobalPatientSearch from "./GlobalPatientSearch";
import ClassNames from "Helpers/Common";

// ONE header row at every breakpoint.
//
// Mobile used to get TWO stacked 64px strips: a logo/hamburger bar from the
// layout, then this row with the search and the account menu. That is a third
// of a phone screen spent on chrome before the page starts, and it put the
// patient search — the thing an admin actually reaches for — on the second
// row. The sidebar toggle and the mark now live here, below lg only; above it
// the sidebar already shows both, so they hide and this row is unchanged.
//
// `onOpenSidebar` comes from whichever layout owns the drawer state.
export default function NavBar({ onOpenSidebar }) {
  const { resetCredentials, getAdminDetail } = useGlobalContext();
  const { name } = getAdminDetail();
  return (
    <Disclosure as="nav" className="bg-white border-b border-gray-300">
      {({ open }) => (
        <>
          <div className="max-w-full flex items-center justify-end h-14 sm:h-16 pl-4 pr-4 sm:pr-6">
            {/* Sidebar toggle + mark: mobile only, where there is no sidebar
                on screen to open it from or to carry the brand. */}
            {onOpenSidebar && (
              <button
                type="button"
                onClick={onOpenSidebar}
                className="lg:hidden -ml-2 mr-1 h-10 w-10 inline-flex items-center justify-center rounded-md sapphire hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-300 flex-shrink-0"
              >
                <span className="sr-only">Open sidebar</span>
                <MenuIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
            <InstaCureLogo className="lg:hidden h-8 w-auto mr-3 flex-shrink-0" />
            {/* Global patient search, to the left of the account menu. */}
            <GlobalPatientSearch />
            <div className="flex justify-end items-center flex-shrink-0 whitespace-nowrap pr-2 sm:static sm:inset-auto sm:pr-0">
              <span className="hidden sm:inline">{name}</span>
              <Menu as="div" className="ml-3 relative">
                <div>
                  <Menu.Button className=" flex text-sm rounded-full focus:outline-none ">
                    <span className="sr-only">Open user menu</span>
                    <ChevronDownIcon className="h-6 w-6 bg-white focus:outline-" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <span
                          onClick={() => {
                            resetCredentials();
                            window.location.reload();
                          }}
                          className={ClassNames(
                            active ? "bg-gray-100" : "",
                            "block px-4 py-2 text-sm text-gray-700"
                          )}
                        >
                          Log out
                        </span>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </>
      )}
    </Disclosure>
  );
}
